import React, { FC, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { Row } from 'components/UI/Row';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import Skeleton from '@material-ui/lab/Skeleton';
import IconButton from '@material-ui/core/IconButton';
import LinkOffOutlined from '@material-ui/icons/LinkOffOutlined';
import DownloadIcon from '@material-ui/icons/CloudDownloadOutlined';
import { AlertDialog } from 'components/UI/Dialog/AlertDialog';
import {
  Document,
  closeError,
  unlinkFromResource,
  downloadDocument,
  fetchDocuments,
  getDocumentsList,
  resetDocuments,
  getDocumentLoaders,
  getDocumentApiErrors
} from './documents';

// TODO: update saving
const DocumentsList: FC<{ id: string; type: string }> = ({ id, type }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const documents = useSelector(getDocumentsList);
  const { isLoading, isDeleting, isUpdating } = useSelector(getDocumentLoaders);
  const { loadingError, deletingError } = useSelector(getDocumentApiErrors);
  const [updateId, setUpdateId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      dispatch(resetDocuments());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchDocuments({ type, id }));
  }, [dispatch, id, type]);

  useEffect(() => {
    if (!isUpdating && !isDeleting) {
      setUpdateId(null);
    }
  }, [isUpdating, isDeleting]);

  const handleDownload = useCallback(
    (docId: string) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      dispatch(downloadDocument(docId));
    },
    [dispatch]
  );

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  if (!isLoading && isEmpty(documents)) {
    const resource = type === 'product' ? t('label.product') : t('label.serial number');
    return (
      <div>
        <span>{t('documents.nothing', { resource: resource.toLowerCase() })}</span>
      </div>
    );
  }

  return (
    <div>
      {map(isLoading ? Array.from(new Array(1)) : documents, (document: Document, index: number) => (
        <Row key={index} alignItems="center" margin="10px 0">
          {document ? (
            <span style={{ width: '80%' }}>
              {document.name}.{document.extension}
            </span>
          ) : (
            <Skeleton variant="text" animation="wave" width="80%" />
          )}
          <Row justifyContent="flex-end">
            {document ? (
              <>
                <IconButton aria-label="download document" color="primary" onClick={handleDownload(document.id)}>
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  disabled={isDeleting && document.id === updateId}
                  aria-label="unlink document"
                  color="secondary"
                  onClick={() => setDeleteId(document.id)}
                >
                  <LinkOffOutlined />
                  {isDeleting && document.id === updateId && (
                    <CircularProgress
                      color="secondary"
                      size={34}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: -17,
                        marginLeft: -17
                      }}
                    />
                  )}
                </IconButton>
              </>
            ) : (
              <>
                <Skeleton animation="wave" variant="circle" width={40} height={40} />
                <Skeleton animation="wave" variant="circle" width={40} height={40} />
                <Skeleton animation="wave" variant="circle" width={40} height={40} />
              </>
            )}
          </Row>
        </Row>
      ))}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(deletingError)}
        autoHideDuration={5000}
        onClose={() => {
          dispatch(closeError());
        }}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {deletingError}
        </Alert>
      </Snackbar>
      <AlertDialog
        handleAccept={() => {
          if (deleteId) {
            dispatch(unlinkFromResource({ documentId: deleteId, linkId: id, linkType: type }));
          }

          setDeleteId(null);
        }}
        handleCancel={() => {
          setDeleteId(null);
        }}
        open={!isEmpty(deleteId)}
        title={t('documents.unlink confirm title')}
        text={t('documents.unlink confirm text')}
      />
    </div>
  );
};

export { DocumentsList };
